import React, { FC, useRef, useEffect, useCallback, memo } from 'react';
import { IdText, ResponseLevel } from '../MaintenanceTriggerForm.slice';
import styles from '../MaintenanceTriggerForm.module.scss';
import Row from './Row';
import { AnnotationStandardDetails } from '../../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { FormDataInitial } from '../formSelector';

type Props = {
  rules: any[];
  standard: AnnotationStandardDetails;
  scrollToStart: boolean;
  responseLevels: ResponseLevel[];
  formData: FormDataInitial;
  columns: IdText[];
  onFindingTypeSelected: (newColumns: IdText[]) => void;
  onScrollToStart: () => void;
  setRules: (newRules: any[]) => void;
};

export const RulesTable: FC<Props> = memo(
  ({ rules, standard, scrollToStart, responseLevels, formData, columns, onFindingTypeSelected, onScrollToStart, setRules }) => {
    const tableRef = useRef<HTMLTableElement>(null);
    const theadRef = useRef<HTMLTableSectionElement | null>(null);
    const tbodyRef = useRef<HTMLTableSectionElement | null>(null);

    const onChange = useCallback(
      (updatedRule: any) => {
        const updatedRules = rules.map(rule => {
          if (rule.index === updatedRule.index) {
            return updatedRule;
          }

          return rule;
        });

        setRules(updatedRules);
      },
      [rules, setRules]
    );

    const onDelete = useCallback(
      (index: number) => {
        setRules(rules.filter(x => x.index !== index));
      },
      [rules, setRules]
    );

    useEffect(() => {
      if (scrollToStart && tableRef.current) {
        tableRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        onScrollToStart();
      }
    });

    const theadRefCallback = useCallback((node: HTMLTableSectionElement) => {
      if (node !== null) {
        theadRef.current = node;
        theadRef.current.onscroll = function () {
          if (tbodyRef.current && theadRef.current) {
            tbodyRef.current.scrollLeft = theadRef.current.scrollLeft;
          }
        };
      }
    }, []);

    const tbodyRefCallback = useCallback((node: HTMLTableSectionElement) => {
      if (node !== null) {
        tbodyRef.current = node;
        tbodyRef.current.onscroll = function () {
          if (tbodyRef.current && theadRef.current) {
            theadRef.current.scrollLeft = tbodyRef.current.scrollLeft;
          }
        };
      }
    }, []);

    return (
      <motion.table
        initial={{ y: -100 }}
        transition={{ staggerChildren: 0.5 }}
        animate={{ y: 0 }}
        ref={tableRef}
        className={classNames('custom-table', styles.tableStyles)}
      >
        <thead ref={theadRefCallback}>
          <tr>
            <th className={styles.actionsColumn} key='actions'>
              Actions
            </th>
            {columns.map(x => (
              <th className={styles.column} key={x.id}>
                {x.text}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={tbodyRefCallback}>
          {rules.map(rule => (
            <Row
              key={rule.index}
              rule={rule}
              standard={standard}
              columns={columns}
              onChange={onChange}
              onFindingTypeSelected={onFindingTypeSelected}
              responseLevels={responseLevels}
              onDeleteRow={onDelete}
              formData={formData}
            />
          ))}
        </tbody>
      </motion.table>
    );
  }
);

export default RulesTable;
